/**
 * Corporate Actions agent — phase 7 of broker integration.
 * Dividends, splits, rights issues, bonus shares, capital repayments, AGMs,
 * name changes, delistings. Tracks days-to-ex-date and estimates impact per
 * position. Nigeria: 10% WHT on cash dividends (FIRS), municipal bond
 * coupons tax-exempt, capital repayments tax-free.
 */

export type ActionKind =
  | 'CASH_DIV' | 'SCRIP_DIV' | 'STOCK_SPLIT' | 'REVERSE_SPLIT'
  | 'RIGHTS_ISSUE' | 'BONUS_ISSUE' | 'CAPITAL_REPAY'
  | 'NAME_CHANGE' | 'DELIST' | 'AGM';

export interface CorporateAction {
  id: string;
  symbol: string;
  kind: ActionKind;
  announcedAt: string;
  exDate: string;
  recordDate: string;
  paymentDate?: string;
  ratePerShareKobo?: string;
  ratio?: { newShares: number; forOld: number };
  newCode?: string;
  withholdingPct?: number;
  description?: string;
}

export function daysUntil(date: string, now: Date = new Date()): number {
  const d = new Date(date);
  return Math.ceil((d.getTime() - now.getTime()) / 86400_000);
}
export type Urgency = 'past' | 'today' | 'imminent' | 'soon' | 'later';
export function urgencyOf(exDate: string, now: Date = new Date()): Urgency {
  const d = daysUntil(exDate, now);
  if (d < 0) return 'past';
  if (d === 0) return 'today';
  if (d <= 3) return 'imminent';
  if (d <= 14) return 'soon';
  return 'later';
}

export interface ActionImpact {
  cashKobo?: bigint;
  shares?: number;
  note?: string;
}
export function impactFor(a: CorporateAction, qty: number): ActionImpact {
  if (qty <= 0) return {};
  switch (a.kind) {
    case 'CASH_DIV': {
      if (!a.ratePerShareKobo) return {};
      const gross = BigInt(a.ratePerShareKobo) * BigInt(qty);
      const wht = (gross * BigInt(a.withholdingPct ?? 10)) / 100n;
      return { cashKobo: gross - wht, note: 'Net of ' + (a.withholdingPct ?? 10) + '% WHT' };
    }
    case 'CAPITAL_REPAY': {
      if (!a.ratePerShareKobo) return {};
      return { cashKobo: BigInt(a.ratePerShareKobo) * BigInt(qty), note: 'Tax-free return of capital' };
    }
    case 'SCRIP_DIV':
    case 'BONUS_ISSUE':
    case 'STOCK_SPLIT': {
      if (!a.ratio) return {};
      return { shares: Math.floor(qty * a.ratio.newShares / a.ratio.forOld) };
    }
    case 'REVERSE_SPLIT': {
      if (!a.ratio) return {};
      return { shares: Math.floor(qty * a.ratio.newShares / a.ratio.forOld), note: 'Fractional shares cashed at closing price' };
    }
    case 'RIGHTS_ISSUE': {
      if (!a.ratio) return {};
      return { shares: Math.floor(qty * a.ratio.newShares / a.ratio.forOld), note: 'You may subscribe at discount price (see description)' };
    }
    default: return {};
  }
}

export function labelOf(kind: ActionKind): string {
  return ({
    CASH_DIV: 'Cash Dividend', SCRIP_DIV: 'Scrip Dividend',
    STOCK_SPLIT: 'Stock Split', REVERSE_SPLIT: 'Reverse Split',
    RIGHTS_ISSUE: 'Rights Issue', BONUS_ISSUE: 'Bonus Issue',
    CAPITAL_REPAY: 'Capital Repayment', NAME_CHANGE: 'Name/Code Change',
    DELIST: 'Delisting', AGM: 'Annual General Meeting',
  } as Record<ActionKind, string>)[kind];
}

export function sortByEx(actions: CorporateAction[]): CorporateAction[] {
  return actions.slice().sort((a, b) => a.exDate.localeCompare(b.exDate));
}

export function seedDemoActions(): CorporateAction[] {
  const today = new Date();
  const iso = (days: number) => {
    const d = new Date(today); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10);
  };
  return [
    { id: 'ca-001', symbol: 'ZENITHBANK', kind: 'CASH_DIV',
      announcedAt: iso(-14), exDate: iso(2), recordDate: iso(3), paymentDate: iso(14),
      ratePerShareKobo: '440', withholdingPct: 10,
      description: 'FY2025 final dividend of \u20A64.40 per share.' },
    { id: 'ca-002', symbol: 'DANGCEM', kind: 'SCRIP_DIV',
      announcedAt: iso(-7), exDate: iso(5), recordDate: iso(6), paymentDate: iso(21),
      ratio: { newShares: 1, forOld: 10 },
      description: '1-for-10 scrip dividend to existing shareholders.' },
    { id: 'ca-003', symbol: 'MTNN', kind: 'CASH_DIV',
      announcedAt: iso(-21), exDate: iso(-3), recordDate: iso(-2), paymentDate: iso(7),
      ratePerShareKobo: '880', withholdingPct: 10,
      description: 'Q4 2025 interim dividend.' },
    { id: 'ca-004', symbol: 'GTCO', kind: 'RIGHTS_ISSUE',
      announcedAt: iso(-10), exDate: iso(9), recordDate: iso(10), paymentDate: iso(35),
      ratio: { newShares: 2, forOld: 5 }, ratePerShareKobo: '6200',
      description: '2-for-5 rights issue at \u20A662.00 per share. Acceptance period: 14 days after ex-date.' },
    { id: 'ca-005', symbol: 'BUACEMENT', kind: 'STOCK_SPLIT',
      announcedAt: iso(-4), exDate: iso(12), recordDate: iso(13), paymentDate: iso(14),
      ratio: { newShares: 2, forOld: 1 },
      description: '2-for-1 stock split. Fractional entitlements cashed at closing ref price.' },
    { id: 'ca-006', symbol: 'AIRTELAFRI', kind: 'AGM',
      announcedAt: iso(-12), exDate: iso(18), recordDate: iso(18),
      description: 'Annual General Meeting. Shareholders of record may vote remotely via the investor portal.' },
    { id: 'ca-007', symbol: 'SEPLAT', kind: 'CASH_DIV',
      announcedAt: iso(-2), exDate: iso(24), recordDate: iso(25), paymentDate: iso(40),
      ratePerShareKobo: '1250', withholdingPct: 10,
      description: 'Interim dividend \u20A612.50 per share.' },
    { id: 'ca-008', symbol: 'FIRSTHOLDCO', kind: 'CAPITAL_REPAY',
      announcedAt: iso(-1), exDate: iso(28), recordDate: iso(29), paymentDate: iso(42),
      ratePerShareKobo: '75',
      description: 'Return of capital following completion of sub-company divestment.' },
  ];
}
